/**
 * Build a context-aware system prompt — single-shot plan mode.
 *
 * The LLM receives the full canvas state (with real node IDs) and is asked
 * to return a single JSON plan. No multi-turn tool calling needed → 1 API call per message.
 */
import type { AgentContext } from './agentContext';

export const TOUR_SELECTORS: Record<string, string> = {
  'node-library': 'Khu vực thư viện node ở sidebar trái của Workflow Builder',
  'canvas-area': 'Khu vực canvas vẽ workflow',
  'nav-schedules': 'Nút điều hướng đến trang Lịch (Schedules) ở header',
};

const TOUR_PHASE_DESC: Record<number, string> = {
  1: 'Giới thiệu giao diện (Welcome, Node Library, Canvas)',
  2: 'Tạo workflow đầu tiên (đặt tên, chọn account, post profile, post group)',
  3: 'Tạo workflow seeding (comment, share, interact)',
  4: 'Xem lịch chạy workflow (Schedules)',
};

export function buildSystemPrompt(ctx: AgentContext): string {
  const lang = ctx.language === 'en' ? 'English' : 'Tiếng Việt';

  const tourLine = ctx.tourActive
    ? `Người dùng đang trong Tour hướng dẫn — bước: "${ctx.tourStepId}" (phase ${ctx.tourPhase}: ${TOUR_PHASE_DESC[ctx.tourPhase ?? 0] || 'N/A'}).`
    : 'Người dùng KHÔNG đang trong tour.';

  // Embed real node IDs so LLM can reference them directly in add_edge
  const canvasJson = JSON.stringify({
    nodes: ctx.workflowNodes, // [{ id, type }]
    edge_count: ctx.workflowEdges,
  });

  const selectorList = Object.entries(TOUR_SELECTORS)
    .map(([sel, desc]) => `  - [data-tour="${sel}"] → ${desc}`)
    .join('\n');

  return `
Bạn là **Tadagram Marketing Assistant** — trợ lý AI của Tadagram Marketing Automation.
Trả lời bằng ${lang}.

## Trạng thái canvas hiện tại
\`\`\`json
${canvasJson}
\`\`\`
- Trang: \`${ctx.currentPage}\`
- ${tourLine}

## Output format — BẮT BUỘC trả về JSON

Luôn trả về một object JSON duy nhất:
\`\`\`json
{
  "plan": [
    { "tool": "add_node", "args": { "node_type": "post_to_group" }, "ref": "$new_0" },
    { "tool": "add_edge", "args": { "source_id": "<id_thực_hoặc_$ref>", "target_id": "$new_0" } }
  ],
  "message": "Câu trả lời ngắn gọn cho người dùng bằng tiếng Việt."
}
\`\`\`

### Quy tắc plan:
- **Không cần thao tác canvas** (hỏi đáp thông thường): \`"plan": []\`
- **Ref system**: Mỗi \`add_node\` gán \`"ref": "$new_N"\` (N tăng dần). Dùng \`"$new_N"\` làm \`source_id\`/\`target_id\` trong \`add_edge\` phía sau.
- **Node ID thực**: Dùng \`id\` từ canvas JSON ở trên cho các node đã có sẵn.
- \`account_selector\` đã có sẵn — KHÔNG thêm lại. Dùng ID của nó từ canvas JSON để nối edge.
- Chỉ dùng các tool: \`add_node\`, \`add_edge\`, \`delete_node\`.
- Không xóa node trừ khi user yêu cầu rõ ràng.

## Domain knowledge
- Workflow = Data Zone (account_selector, brand_info, seeding_contents_manager) + node action.
- Node action — nhóm theo category:
  - **with profile** (hành động trên tài khoản cá nhân): post_to_profile, share_url, interact, view_notifications, accept_friend_requests, search_add_friend, check_message
  - **with group** (hành động liên quan nhóm): join_groups, find_group_posts, find_top_posts, post_to_group
  - **with post** (tương tác với bài đăng): comment_on_post, like_posts, reply_to_comments, filter_post, find_trend_fb
  - **with page** (Fanpage qua Graph API, không cần login tài khoản): post_to_fanpage, fanpage_comment_on_post, fanpage_reply_to_comments, fanpage_like_comments, fanpage_moderate_comments, fanpage_private_reply, like_pages
  - **messaging** (nhắn tin / auto-reply): send_direct_message, post_auto_responder, group_random_messenger
  - **ai_flow_action** (AI xử lý nội dung): ai_generate_asset, ai_remake_image, ai_text_process

## Selectors UI (highlight)
${selectorList}
`.trim();
}
